import { crypto } from 'bitcoinjs-lib';
import { ECPairFactory, ECPairInterface } from 'ecpair';
import * as ecc from 'tiny-secp256k1';

import {
  History,
  HistoryArrayFlags,
  HistoryEntry,
  HistoryEntryJSON,
  HistoryJSON,
} from './types.js';

/**
 * Finds a history entry by its hash or by the number of steps from the current head.
 */
export const historyFindByStep = <TState, TMeta>(
  history: History<TState, TMeta>,
  steps: number | string
): HistoryEntry<TState, TMeta> => {
  if (typeof steps === 'number') {
    let head = history.head;
    if (steps > 0) {
      while (head.next && steps > 0) {
        head = head.next;
        steps--;
      }
    } else if (steps < 0) {
      while (head.prev && steps < 0) {
        head = head.prev;
        steps++;
      }
    }

    return head;
  }

  // Binary search
  let left: HistoryEntry<TState, TMeta> | null = history.head;
  let right: HistoryEntry<TState, TMeta> | null = history.head;
  while (left || right) {
    if (left?.hash === steps) {
      return left;
    } else if (right?.hash === steps) {
      return right;
    }

    left = left?.prev ?? null;
    right = right?.next ?? null;
  }

  throw new Error(`History entry with hash ${steps} not found`);
};

export const historyToGenerator = function* <TState, TMeta>(head: HistoryEntry<TState, TMeta>) {
  let first: HistoryEntry<TState, TMeta> = historyGetFirst(head);

  for (
    let current: HistoryEntry<TState, TMeta> | null = first;
    current !== null;
    current = current.next
  ) {
    yield current;
  }
};

export const historyGetFirst = <TState, TMeta>(head: HistoryEntry<TState, TMeta>) => {
  let first: HistoryEntry<TState, TMeta> | null = head;
  for (
    let current: HistoryEntry<TState, TMeta> | null = head;
    current !== null;
    current = current.prev
  ) {
    first = current;
  }
  return first;
};

/**
 * Fast way to convert history to array
 */
export const historyToArray = <TState, TMeta>(
  head: HistoryEntry<TState, TMeta>,
  flags = HistoryArrayFlags.All
) => {
  const leftSide: HistoryEntry<TState, TMeta>[] = [];
  const rightSide: HistoryEntry<TState, TMeta>[] = [];

  let leftLeaf: HistoryEntry<TState, TMeta> | null = head;
  let rightLeaf: HistoryEntry<TState, TMeta> | null = head;

  const computeLeft = flags & HistoryArrayFlags.Left;
  const computeRight = flags & HistoryArrayFlags.Right;

  let first = true;

  while ((computeLeft && leftLeaf) || (computeRight && rightLeaf)) {
    if (computeLeft) {
      if (leftLeaf) {
        leftSide.unshift(leftLeaf);
      }
      leftLeaf = leftLeaf?.prev ?? null;
    }

    if (computeRight) {
      if (rightLeaf && !first) {
        rightSide.push(rightLeaf);
      }
      rightLeaf = rightLeaf?.next ?? null;
    }

    first = false;
  }

  return [...leftSide, ...rightSide];
};

export const historyFromJSON = <TState, TMeta>(
  history: History<TState, TMeta>,
  json: HistoryJSON<TState, TMeta>
) => {
  let head: HistoryEntry<TState, TMeta> | undefined = undefined;
  let prev: HistoryEntry<TState, TMeta> | undefined = undefined;

  const entries: HistoryEntryJSON<TState, TMeta>[] = json.entries;
  const headRef: string = json.head;
  const signature: string = json.signature;

  if (typeof headRef !== 'string' || typeof signature !== 'string' || !Array.isArray(entries)) {
    throw new Error('Invalid history JSON');
  }

  let size = 0;
  entries.forEach((jsonEntry) => {
    const { state, meta } = jsonEntry;

    const entry = historyCreateEntry(state, meta, prev);
    size++;
    if (entry.hash === headRef) {
      head = entry;
    }

    prev = entry;
  });

  if (!head) {
    throw new Error('Invalid history JSON empty head');
  }

  history.size = size;
  history.head = head;
  history.signature = signature;

  if (!history.verify(signature)) {
    throw new Error('Invalid history JSON signature');
  }

  return history;
};

export const historyToJSON = <TState, TMeta>(
  history: History<TState, TMeta>
): HistoryJSON<TState, TMeta> => {
  const entries = historyToArray(history.head).map(({ state, meta }) => ({ state, meta }));

  return {
    head: history.head.hash,
    entries,
    signature: history.signature,
  };
};

export const historyCreateEntry = <TState, TMeta>(
  state: TState,
  meta?: TMeta,
  prev: HistoryEntry<TState, TMeta> | null = null
) => {
  let buffer = Buffer.from(JSON.stringify(state));

  if (prev) {
    buffer = Buffer.concat([buffer, Buffer.from(prev.hash, 'base64')]);
  }

  const hash = crypto.hash256(buffer);

  const entry: HistoryEntry<TState, TMeta> = {
    hash: hash.toString('base64'),
    index: prev ? prev.index + 1 : 0,
    state,
    meta,
    next: null,
    prev,
  };

  if (prev) {
    prev.next = entry;
  }

  return entry;
};

export const historyChecksum = <TState, TMeta>(head: HistoryEntry<TState, TMeta>) =>
  crypto.sha256(
    historyToArray(head).reduce(
      (acc, { hash }) => Buffer.concat([acc, Buffer.from([0x63]), Buffer.from(hash, 'base64')]),
      Buffer.from([])
    )
  );

export const historySignature = <TState, TMeta>(
  keyPair: ECPairInterface,
  head: HistoryEntry<TState, TMeta>
): string => {
  return keyPair.sign(historyChecksum(head)).toString('base64');
};

export const historyVerify = <TState, TMeta>(
  keyPair: ECPairInterface,
  head: HistoryEntry<TState, TMeta>,
  signature: string
): boolean => {
  return keyPair.verify(historyChecksum(head), Buffer.from(signature, 'base64'));
};

export const historyCreateKeyPair = (wif?: string) => {
  const ECPair = ECPairFactory(ecc);

  return wif ? ECPair.fromWIF(wif) : ECPair.makeRandom();
};
